// US Letter at 300 DPI. The preview element is laid out at full print
// size (1 CSS px = 1 printed dot) and captured 1:1 into the PDF.
export const LETTER_WIDTH_INCHES = 8.5;
export const LETTER_HEIGHT_INCHES = 11;
export const DPI = 300;
export const LETTER_WIDTH_PX = LETTER_WIDTH_INCHES * DPI; // 2550
export const LETTER_HEIGHT_PX = LETTER_HEIGHT_INCHES * DPI; // 3300
/** Half-inch margin on every side, inside the captured page. */
export const PAGE_MARGIN_PX = 0.5 * DPI; // 150

/**
 * Waits for web fonts and every image inside `root`, so the capture never
 * shows fallbacks, but never longer than `timeoutMs`: a slow or broken image
 * must not block the PDF.
 */
export const whenRenderReady = (root: HTMLElement, timeoutMs = 4000): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>(resolve => {
    timer = setTimeout(resolve, timeoutMs);
  });
  return Promise.race([waitForFontsAndImages(root), timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  });
};

const waitForFontsAndImages = async (root: HTMLElement): Promise<void> => {
  try {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts && fonts.ready) await fonts.ready;
  } catch {
    // Font loading API missing: capture anyway.
  }
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(img => {
      if (typeof img.decode === 'function') return img.decode().catch(() => undefined);
      if (img.complete) return Promise.resolve();
      return new Promise<void>(resolve => {
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
      });
    }),
  );
};
